import { useState, useEffect, useCallback } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Shield, FileText, PenLine, Download, CheckCircle, AlertTriangle, LogIn } from 'lucide-react'
import { useAuth } from '@/shared/contexts/AuthContext'
import {
  getEnvelopeByToken,
  signWithToken,
  signEnvelopePki,
  claimRecipient,
  type EnvelopeByToken,
} from '@/features/documents/services/envelopeService'
import {
  getStoredSignatures,
  type StoredSignature,
} from '@/features/my-signatures/services/signatureStorage'
import { SignaturePad } from '@/features/my-signatures/components/SignaturePad'
import {
  embedSignatureInPdf,
  base64ToUint8Array,
  uint8ArrayToBase64,
  createPdfBlobUrl,
  downloadPdf,
} from '@/shared/lib/pdf-utils'
import { saveSignature } from '@/features/my-signatures/services/signatureStorage'
import { PkiSignPanel } from './PkiSignPanel'
import toast from 'react-hot-toast'

type ViewState = 'loading' | 'login_required' | 'review' | 'sign' | 'signing' | 'done' | 'error' | 'already_signed'

export function SignInvitation() {
  const { token } = useParams<{ token: string }>()
  const { user, loading: authLoading } = useAuth()

  const [viewState, setViewState] = useState<ViewState>('loading')
  const [envelopeData, setEnvelopeData] = useState<EnvelopeByToken | null>(null)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [signatures, setSignatures] = useState<StoredSignature[]>([])
  const [selectedSignature, setSelectedSignature] = useState<StoredSignature | null>(null)
  const [showNewSignature, setShowNewSignature] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const [signedDocBase64, setSignedDocBase64] = useState<string | null>(null)

  // Load envelope data
  const loadEnvelope = useCallback(async () => {
    if (!token) return
    try {
      const data = await getEnvelopeByToken(token)
      if (!data) {
        setErrorMsg('Link de firma inválido o expirado')
        setViewState('error')
        return
      }
      setEnvelopeData(data)

      if (data.recipient.status === 'signed') {
        setViewState('already_signed')
        if (data.envelope.document_data) {
          setPdfUrl(createPdfBlobUrl(data.envelope.document_data))
        }
        return
      }

      if (data.envelope.document_data) {
        setPdfUrl(createPdfBlobUrl(data.envelope.document_data))
      }

      if (!user) {
        setViewState('login_required')
      } else {
        // Claim recipient (link user to this invitation)
        await claimRecipient(token, user.id)
        setViewState('review')
      }
    } catch (err) {
      console.error('Error loading envelope:', err)
      setErrorMsg('Error al cargar el documento')
      setViewState('error')
    }
  }, [token, user])

  useEffect(() => {
    if (authLoading) return
    loadEnvelope()
  }, [authLoading, loadEnvelope])

  // Load user signatures when in review/sign state
  useEffect(() => {
    if (user && (viewState === 'review' || viewState === 'sign')) {
      getStoredSignatures().then(setSignatures).catch(console.error)
    }
  }, [user, viewState])

  const handleNewSignatureSaved = async (imageData: string, type: 'draw' | 'upload') => {
    if (!user) return
    try {
      const saved = await saveSignature({
        userId: user.id,
        name: 'Mi Firma',
        signatureType: type,
        imageData,
        isDefault: true,
      })
      setSignatures(prev => [saved, ...prev])
      setSelectedSignature(saved)
      setShowNewSignature(false)
      toast.success('Firma guardada')
    } catch (err) {
      console.error('Error saving signature:', err)
      toast.error('Error al guardar firma')
    }
  }

  const handleSign = async () => {
    if (!token || !user || !selectedSignature || !envelopeData) return

    setViewState('signing')
    try {
      const pdfBytes = base64ToUint8Array(envelopeData.envelope.document_data)
      const displayName = user.user_metadata?.full_name || user.email || envelopeData.recipient.name

      const signedBytes = await embedSignatureInPdf(
        pdfBytes,
        selectedSignature.image_data,
        displayName,
        'right'
      )

      const signedBase64 = uint8ArrayToBase64(signedBytes)

      await signWithToken({
        token,
        userId: user.id,
        signatureImage: selectedSignature.image_data,
        signedDocument: signedBase64,
      })

      setSignedDocBase64(signedBase64)
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      setPdfUrl(createPdfBlobUrl(signedBase64))
      setViewState('done')
      toast.success('Documento firmado exitosamente')
    } catch (err) {
      console.error('Error signing:', err)
      toast.error('Error al firmar el documento')
      setViewState('sign')
    }
  }

  const handlePkiSign = async (p12File: File, password: string) => {
    if (!token || !envelopeData) return
    setViewState('signing')
    try {
      const displayName = user?.user_metadata?.full_name || user?.email || envelopeData.recipient.name
      const result = await signEnvelopePki({
        token,
        p12File,
        password,
        signerName: displayName,
        reason: `Firma de ${envelopeData.envelope.title}`,
        location: envelopeData.envelope.country === 'EC' ? 'Ecuador' : 'Argentina',
      })

      if (!result.success) throw new Error('Error al firmar')

      // Reload the envelope to get the updated signed PDF
      const updated = await getEnvelopeByToken(token)
      if (updated?.envelope.document_data) {
        if (pdfUrl) URL.revokeObjectURL(pdfUrl)
        setPdfUrl(createPdfBlobUrl(updated.envelope.document_data))
        setSignedDocBase64(updated.envelope.document_data)
      }

      setViewState('done')
      toast.success(result.message || 'Documento firmado exitosamente')
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error al firmar'
      toast.error(msg)
      setViewState('sign')
    }
  }

  const handleDownload = () => {
    const docData = signedDocBase64 || envelopeData?.envelope.document_data
    if (!docData || !envelopeData) return
    const filename = envelopeData.envelope.original_filename.replace(/\.pdf$/i, '_firmado.pdf')
    downloadPdf(docData, filename)
  }

  // Header bar
  const Header = () => (
    <header className="bg-white dark:bg-legal-800 border-b border-legal-200 dark:border-legal-700 px-4 sm:px-6 py-3 sm:py-4">
      <div className="max-w-4xl mx-auto flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary-600 flex items-center justify-center">
          <Shield className="text-white" size={24} />
        </div>
        <div>
          <h1 className="text-lg font-bold">
            <span className="text-legal-900 dark:text-white">Sign</span>
            <span className="text-primary-600 dark:text-primary-400">Wiz</span>
          </h1>
          <p className="text-xs text-legal-500 dark:text-legal-400">Firma de Documento</p>
        </div>
      </div>
    </header>
  )

  // Loading
  if (viewState === 'loading' || authLoading) {
    return (
      <div className="min-h-screen bg-legal-50 dark:bg-legal-900">
        <Header />
        <div className="text-center py-20 text-legal-500 dark:text-legal-400">
          Cargando documento...
        </div>
      </div>
    )
  }

  // Error
  if (viewState === 'error') {
    return (
      <div className="min-h-screen bg-legal-50 dark:bg-legal-900">
        <Header />
        <div className="max-w-md mx-auto text-center py-20">
          <AlertTriangle size={48} className="mx-auto text-red-400 mb-4" />
          <p className="text-legal-700 dark:text-legal-200 mb-4">{errorMsg}</p>
          <Link to="/" className="text-primary-600 dark:text-primary-400 text-sm hover:underline">
            Ir a SignWiz
          </Link>
        </div>
      </div>
    )
  }

  // Login required
  if (viewState === 'login_required') {
    return (
      <div className="min-h-screen bg-legal-50 dark:bg-legal-900">
        <Header />
        <div className="max-w-lg mx-auto py-12 px-4">
          {envelopeData && (
            <div className="bg-white dark:bg-legal-800 rounded-xl border border-legal-200 dark:border-legal-700 p-6 mb-6 text-center">
              <FileText size={40} className="mx-auto text-primary-500 mb-3" />
              <h2 className="text-lg font-bold text-legal-900 dark:text-white mb-2">
                {envelopeData.envelope.title}
              </h2>
              <p className="text-sm text-legal-500 dark:text-legal-400 mb-1">
                De: {envelopeData.sender.display_name || envelopeData.sender.email}
              </p>
              {envelopeData.envelope.message && (
                <p className="text-sm text-legal-600 dark:text-legal-300 mt-3 p-3 bg-legal-50 dark:bg-legal-900 rounded-lg">
                  "{envelopeData.envelope.message}"
                </p>
              )}
            </div>
          )}

          <div className="bg-white dark:bg-legal-800 rounded-xl border border-legal-200 dark:border-legal-700 p-6 text-center">
            <LogIn size={32} className="mx-auto text-primary-500 mb-3" />
            <h3 className="font-semibold text-legal-900 dark:text-white mb-2">
              Inicia sesion para firmar
            </h3>
            <p className="text-sm text-legal-500 dark:text-legal-400 mb-4">
              Necesitas una cuenta en SignWiz para revisar y firmar este documento.
            </p>
            <Link
              to={`/login?redirect=/firmar-invitacion/${token}`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium"
            >
              <LogIn size={18} />
              Iniciar Sesión / Registrarse
            </Link>
          </div>
        </div>
      </div>
    )
  }

  // Already signed
  if (viewState === 'already_signed') {
    return (
      <div className="min-h-screen bg-legal-50 dark:bg-legal-900">
        <Header />
        <div className="max-w-4xl mx-auto py-12 px-4">
          <div className="text-center mb-6">
            <CheckCircle size={48} className="mx-auto text-green-500 mb-3" />
            <h2 className="text-xl font-bold text-legal-900 dark:text-white">
              Este documento ya fue firmado
            </h2>
            {envelopeData?.recipient.signed_at && (
              <p className="text-sm text-legal-500 dark:text-legal-400 mt-1">
                Firmado el {new Date(envelopeData.recipient.signed_at).toLocaleDateString('es-AR')}
              </p>
            )}
          </div>
          {pdfUrl && (
            <div className="bg-white dark:bg-legal-800 rounded-xl border border-legal-200 dark:border-legal-700 overflow-hidden mb-4">
              <iframe src={pdfUrl} className="w-full h-[250px] sm:h-[400px] md:h-[500px] border-0" title="Documento firmado" />
            </div>
          )}
          <div className="text-center">
            <button
              onClick={handleDownload}
              className="inline-flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
            >
              <Download size={16} />
              Descargar PDF
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Review & Sign
  return (
    <div className="min-h-screen bg-legal-50 dark:bg-legal-900">
      <Header />
      <div className="max-w-4xl mx-auto py-8 px-4">
        {/* Document info */}
        {envelopeData && (
          <div className="bg-white dark:bg-legal-800 rounded-xl border border-legal-200 dark:border-legal-700 p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-bold text-legal-900 dark:text-white">
                  {envelopeData.envelope.title}
                </h2>
                <p className="text-sm text-legal-500 dark:text-legal-400">
                  De: {envelopeData.sender.display_name || envelopeData.sender.email}
                </p>
              </div>
              {viewState === 'review' && (
                <button
                  onClick={() => setViewState('sign')}
                  className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium"
                >
                  <PenLine size={16} />
                  Firmar
                </button>
              )}
            </div>
            {envelopeData.envelope.message && (
              <p className="text-sm text-legal-600 dark:text-legal-300 mt-3 p-3 bg-legal-50 dark:bg-legal-900 rounded-lg">
                "{envelopeData.envelope.message}"
              </p>
            )}
          </div>
        )}

        {/* PDF Preview */}
        {pdfUrl && (
          <div className="bg-white dark:bg-legal-800 rounded-xl border border-legal-200 dark:border-legal-700 overflow-hidden mb-6">
            <div className="px-4 py-3 border-b border-legal-200 dark:border-legal-700">
              <p className="text-sm font-medium text-legal-700 dark:text-legal-200 flex items-center gap-2">
                <FileText size={16} />
                {envelopeData?.envelope.original_filename}
              </p>
            </div>
            <iframe
              src={pdfUrl}
              className="w-full h-[250px] sm:h-[400px] border-0"
              title="Documento a firmar"
            />
          </div>
        )}

        {/* Sign panel */}
        {(viewState === 'sign' || viewState === 'signing') && (
          <div className="bg-white dark:bg-legal-800 rounded-xl border border-primary-200 dark:border-primary-700 p-4 sm:p-6">
            {envelopeData?.envelope.signing_method === 'pkcs12' ? (
              <>
                <h3 className="text-lg font-semibold text-legal-900 dark:text-white mb-4">
                  Firmar con Certificado Digital
                </h3>
                <PkiSignPanel
                  onSign={handlePkiSign}
                  country={envelopeData.envelope.country}
                  disabled={viewState === 'signing'}
                />
              </>
            ) : (
              <>
                <h3 className="text-lg font-semibold text-legal-900 dark:text-white mb-4">
                  Selecciona o crea tu firma
                </h3>

                {showNewSignature ? (
                  <SignaturePad
                    onSave={handleNewSignatureSaved}
                    onCancel={() => setShowNewSignature(false)}
                  />
                ) : (
                  <>
                    {signatures.length > 0 ? (
                      <div className="grid grid-cols-2 gap-3 mb-4">
                        {signatures.map((sig) => (
                          <button
                            key={sig.id}
                            onClick={() => setSelectedSignature(sig)}
                            className={`p-3 rounded-lg border-2 transition-colors ${
                              selectedSignature?.id === sig.id
                                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                                : 'border-legal-200 dark:border-legal-700 hover:border-legal-300'
                            }`}
                          >
                            <img src={sig.image_data} alt={sig.name} className="h-12 object-contain" />
                            <p className="text-xs text-legal-500 dark:text-legal-400 mt-1">{sig.name}</p>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-legal-500 dark:text-legal-400 mb-4">
                        No tenes firmas guardadas. Crea una para firmar el documento.
                      </p>
                    )}

                    <div className="flex gap-3">
                      <button
                        onClick={() => setShowNewSignature(true)}
                        className="px-4 py-2 text-sm border border-legal-300 dark:border-legal-600 text-legal-600 dark:text-legal-300 rounded-lg hover:bg-legal-50 dark:hover:bg-legal-700"
                      >
                        {signatures.length > 0 ? 'Nueva firma' : 'Crear firma'}
                      </button>
                      <button
                        onClick={handleSign}
                        disabled={!selectedSignature || viewState === 'signing'}
                        className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <PenLine size={16} />
                        {viewState === 'signing' ? 'Firmando...' : 'Firmar Documento'}
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        )}

        {/* Done */}
        {viewState === 'done' && (
          <div className="text-center bg-white dark:bg-legal-800 rounded-xl border border-green-200 dark:border-green-800 p-8">
            <CheckCircle size={48} className="mx-auto text-green-500 mb-3" />
            <h2 className="text-xl font-bold text-legal-900 dark:text-white mb-2">
              Documento firmado exitosamente
            </h2>
            <p className="text-sm text-legal-500 dark:text-legal-400 mb-6">
              Tu firma fue aplicada al documento. Podés descargarlo ahora.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={handleDownload}
                className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium"
              >
                <Download size={16} />
                Descargar PDF Firmado
              </button>
              <Link
                to="/documentos"
                className="px-4 py-2 text-sm text-legal-600 dark:text-legal-300 hover:bg-legal-50 dark:hover:bg-legal-700 rounded-lg"
              >
                Ir a Mis Documentos
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
